import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie, ShieldCheck, BarChart3, Megaphone, Lock } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { LangToggle } from "./Navbar";

const LS_KEY = "zirsen-cookie-consent";

export const CookieConsent = () => {
  const { t, lang } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [settings, setSettings] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);
  const c = t.cookies;

  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) return;
    if (localStorage.getItem(LS_KEY)) return;
    const tmr = setTimeout(() => setVisible(true), 1600);
    return () => clearTimeout(tmr);
  }, []);

  const store = (a, m) => {
    localStorage.setItem(LS_KEY, JSON.stringify({ necessary: true, analytics: a, marketing: m, ts: Date.now() }));
    window.dispatchEvent(new Event("zirsen-consent"));
    setVisible(false);
  };

  const Toggle = ({ on, onClick, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`relative h-6 w-11 shrink-0 rounded-full border transition-colors duration-200 ${
        on ? "border-teal-300 bg-teal-500" : "border-slate-600 bg-white/5"
      }`}
    >
      <span
        className={`absolute top-0.5 h-4.5 w-4.5 rounded-full bg-white transition-all duration-200 ${on ? "left-[22px]" : "left-0.5"}`}
        style={{ height: 18, width: 18 }}
      />
    </button>
  );

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="cookie-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.35 } }}
          className="fixed inset-0 z-[90] bg-[#03070D]/70 backdrop-blur-sm"
          data-testid="cookie-backdrop"
        >
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-6">
            <motion.div
              key="cookie-card"
              initial={{ y: 140, scale: 0.9, opacity: 0, rotate: -1.5 }}
              animate={{ y: 0, scale: 1, opacity: 1, rotate: 0 }}
              exit={{ y: 120, scale: 0.92, opacity: 0, rotate: 1.5, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } }}
              transition={{ type: "spring", stiffness: 210, damping: 19 }}
              className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-teal-400/30 bg-[#071120] shadow-[0_0_80px_rgba(45,212,191,0.25)]"
              data-testid="cookie-consent"
            >
              <div className="footer-grid absolute inset-0" aria-hidden="true" />
              <div className="hero-scanline" aria-hidden="true" />
              <div className="laser-divider absolute inset-x-0 top-0" aria-hidden="true" />

              <div className="relative p-7 sm:p-9">
                <div className="flex items-start justify-between gap-4">
                  <motion.div
                    initial={{ rotate: -20, scale: 0 }}
                    animate={{ rotate: 0, scale: 1 }}
                    transition={{ type: "spring", stiffness: 260, damping: 12, delay: 0.25 }}
                    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-teal-400/40 bg-teal-400/10 text-teal-300 shadow-[0_0_24px_rgba(45,212,191,0.3)]"
                  >
                    <Cookie size={26} />
                  </motion.div>
                  <div className="flex items-center gap-2.5">
                    <LangToggle />
                    <span className="rounded-full border border-teal-400/30 bg-teal-400/10 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-teal-300">
                      GDPR
                    </span>
                  </div>
                </div>

                <h2 className="font-heading mg-glow-text mt-5 text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl" data-testid="cookie-title">
                  {c.title[lang]}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.text[lang]}</p>

                <AnimatePresence initial={false}>
                  {settings && (
                    <motion.div
                      key="cookie-settings"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                      data-testid="cookie-settings"
                    >
                      <div className="mt-5 space-y-3">
                        <div className="flex items-center justify-between gap-4 rounded-xl border border-teal-400/15 bg-white/[0.03] p-3.5">
                          <span className="flex items-center gap-2.5 text-sm font-semibold text-slate-200">
                            <Lock size={15} className="text-teal-300" /> {c.necessary[lang]}
                          </span>
                          <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-teal-400/70">{c.alwaysOn[lang]}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4 rounded-xl border border-teal-400/15 bg-white/[0.03] p-3.5">
                          <span className="flex items-center gap-2.5 text-sm font-semibold text-slate-200">
                            <BarChart3 size={15} className="text-teal-300" /> {c.analytics[lang]}
                          </span>
                          <Toggle on={analytics} onClick={() => setAnalytics(!analytics)} testid="cookie-toggle-analytics" />
                        </div>
                        <div className="flex items-center justify-between gap-4 rounded-xl border border-teal-400/15 bg-white/[0.03] p-3.5">
                          <span className="flex items-center gap-2.5 text-sm font-semibold text-slate-200">
                            <Megaphone size={15} className="text-teal-300" /> {c.marketing[lang]}
                          </span>
                          <Toggle on={marketing} onClick={() => setMarketing(!marketing)} testid="cookie-toggle-marketing" />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="mt-7 flex flex-col gap-2.5 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => store(true, true)}
                    data-testid="cookie-accept-all"
                    className="flex-1 rounded-full bg-teal-500 py-3.5 text-sm font-bold uppercase tracking-widest text-[#052E2B] transition-all duration-200 hover:bg-teal-300 hover:shadow-[0_0_30px_rgba(45,212,191,0.45)]"
                  >
                    {c.acceptAll[lang]}
                  </button>
                  <button
                    type="button"
                    onClick={() => (settings ? store(analytics, marketing) : setSettings(true))}
                    data-testid="cookie-settings-button"
                    className="flex-1 rounded-full border border-teal-400/40 py-3.5 text-sm font-bold uppercase tracking-widest text-teal-300 transition-colors duration-200 hover:bg-teal-400/10"
                  >
                    {settings ? c.save[lang] : c.settings[lang]}
                  </button>
                  <button
                    type="button"
                    onClick={() => store(false, false)}
                    data-testid="cookie-reject"
                    className="rounded-full border border-slate-600 px-6 py-3.5 text-sm font-bold uppercase tracking-widest text-slate-400 transition-colors duration-200 hover:border-slate-400 hover:text-slate-200"
                  >
                    {c.reject[lang]}
                  </button>
                </div>

                <a href="/ochrana-osobnich-udaju" data-testid="cookie-privacy-link" className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-slate-500 transition-colors hover:text-teal-300">
                  <ShieldCheck size={12} /> {c.privacyLink[lang]}
                </a>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
